<template>
	<div class="mall-store-home">
		<section class="store-hero">
			<div class="hero-inner">
				<div class="hero-content">
					<span class="hero-eyebrow">CHU JIAN TONG SERVICE MALL</span>
					<h1>楚建通服务商城</h1>
					<p>汇聚平台自营数字服务与优质服务商方案，让服务选择更简单、交付更安心。</p>
					<div class="hero-search">
						<i class="el-icon-search"></i>
						<input v-model.trim="searchKeyword" placeholder="搜索商品或服务" @keyup.enter="handleSearch" />
						<button type="button" :disabled="loading" @click="handleSearch">
							{{ loading ? '搜索中...' : '搜索' }}
						</button>
					</div>
					<div class="hot-keywords">
						<span>热门搜索</span>
						<button v-for="keyword in hotSearchList" :key="keyword" type="button" @click="handleHotSearch(keyword)">
							{{ keyword }}
						</button>
					</div>
				</div>
				<div class="hero-visual" aria-hidden="true">
					<div class="visual-ring ring-one"></div>
					<div class="visual-ring ring-two"></div>
					<div class="visual-card visual-card-main">
						<i class="el-icon-s-platform"></i>
						<strong>专业服务</strong>
						<span>一站式选购</span>
					</div>
					<div class="visual-card visual-card-small card-self">
						<i class="el-icon-s-check"></i>
						平台自营
					</div>
					<div class="visual-card visual-card-small card-show">
						<i class="el-icon-chat-dot-round"></i>
						服务展销
					</div>
				</div>
			</div>
		</section>

		<main class="store-main">
			<section class="service-feature-list">
				<div v-for="feature in serviceFeatures" :key="feature.title" class="service-feature">
					<span class="feature-icon"><i :class="feature.icon"></i></span>
					<div>
						<strong>{{ feature.title }}</strong>
						<p>{{ feature.description }}</p>
					</div>
				</div>
			</section>

			<section class="goods-section">
				<div class="section-heading">
					<div>
						<span class="section-kicker">SERVICE SELECTION</span>
						<h2>精选服务商品</h2>
						<p>自营商品在线选购，展销商品一键咨询</p>
					</div>
					<div class="type-tabs" role="tablist" aria-label="服务商品类型筛选">
						<button
							v-for="tab in typeTabs"
							:key="tab.value"
							type="button"
							role="tab"
							:class="{ active: activeType === tab.value }"
							:aria-selected="activeType === tab.value"
							@click="activeType = tab.value">
							{{ tab.label }}
						</button>
					</div>
				</div>

				<div v-loading="loading" class="goods-list" element-loading-text="正在加载商品...">
					<article
						v-for="(goods, index) in visibleGoodsList"
						:key="goods.goodsId || index"
						class="goods-card"
						tabindex="0"
						@click="handleGoodsClick(goods)"
						@keyup.enter="handleGoodsClick(goods)">
						<div class="goods-image-wrap">
							<img :src="goods.image" :alt="goods.title" @error="handleImageError" />
							<span :class="['goods-type', goods.goodsType === 1 ? 'is-self' : 'is-exhibition']">
								<i :class="goods.goodsType === 1 ? 'el-icon-s-check' : 'el-icon-s-promotion'"></i>
								{{ goods.goodsType === 1 ? '平台自营' : '展销商品' }}
							</span>
						</div>
						<div class="goods-body">
							<span class="goods-category">{{ goods.category }}</span>
							<h3>{{ goods.title }}</h3>
							<p class="goods-summary">{{ goods.summary }}</p>
							<div class="goods-footer">
								<div v-if="goods.goodsType === 1" class="goods-price">
									<small>¥</small>
									<strong>{{ goods.price }}</strong>
									<span>起</span>
								</div>
								<div v-else class="consult-tip">
									<i class="el-icon-service"></i>
									专属顾问为您服务
								</div>
								<span class="detail-link">
									查看详情
									<i class="el-icon-right"></i>
								</span>
							</div>
						</div>
					</article>
					<div v-if="!loading && !visibleGoodsList.length" class="goods-empty">
						<span><i class="el-icon-goods"></i></span>
						<strong>暂未找到相关商品</strong>
						<p>换个关键词或商品类型试试</p>
						<el-button type="primary" plain @click="clearSearch">查看全部商品</el-button>
					</div>
				</div>
			</section>
		</main>
	</div>
</template>

<script>
	import { previewMerchantFile, queryPublishedMerchantGoodsList } from '@/api/mall'

	const DEFAULT_GOODS_IMAGE = require('@/assets/mall/goods/goods-demo.png')

	export default {
		name: 'MallStoreHome',
		data() {
			return {
				loading: false,
				searchKeyword: '',
				activeType: 0,
				hotSearchList: ['资质服务', '企业宣传', '电子签章', '人才服务'],
				typeTabs: [
					{ label: '全部商品', value: 0 },
					{ label: '平台自营', value: 1 },
					{ label: '展销商品', value: 2 }
				],
				serviceFeatures: [
					{ title: '平台甄选', description: '精选服务商品，品质更有保障', icon: 'el-icon-medal' },
					{ title: '交易便捷', description: '自营服务支持在线选购与下单', icon: 'el-icon-shopping-bag-2' },
					{ title: '专业对接', description: '展销服务提供专属咨询通道', icon: 'el-icon-service' }
				],
				goodsList: []
			}
		},
		computed: {
			visibleGoodsList() {
				if (!this.activeType) return this.goodsList
				return this.goodsList.filter(item => item.goodsType === this.activeType)
			}
		},
		created() {
			this.loadGoods()
		},
		methods: {
			// 沿用原商城首页的已上架商品查询接口，仅重新组织页面展示字段。
			async loadGoods() {
				this.loading = true
				try {
					const response = await queryPublishedMerchantGoodsList({ goodsTitle: this.searchKeyword.trim() })
					const records = Array.isArray(response.data) ? response.data : []
					this.goodsList = records.map(this.mapGoodsCard)
				} finally {
					this.loading = false
				}
			},
			mapGoodsCard(goods) {
				const goodsType = Number(goods.goodsType) === 2 ? 2 : 1
				return {
					goodsId: goods.id,
					goodsType,
					image: goods.mainImage ? previewMerchantFile() + goods.mainImage : DEFAULT_GOODS_IMAGE,
					title: goods.goodsTitle || goods.guideTitle || '未命名服务商品',
					summary: goods.goodsIntroduction || goods.goodsIntro || goods.guideTitle || '专业服务方案，详情请进入商品页面查看。',
					category:
						goods.categoryThirdName || goods.categorySecondName || goods.categoryFirstName || goods.categoryName || '综合服务',
					price: this.formatPrice(goods.sellingPrice),
					contactPhone: goods.contactPhone || ''
				}
			},
			formatPrice(value) {
				if (value === undefined || value === null || value === '') return '0.00'
				const price = Number(value)
				return Number.isNaN(price) ? String(value) : price.toFixed(2)
			},
			handleSearch() {
				this.loadGoods()
			},
			handleHotSearch(keyword) {
				this.searchKeyword = keyword
				this.loadGoods()
			},
			clearSearch() {
				this.searchKeyword = ''
				this.activeType = 0
				this.loadGoods()
			},
			// goodsType 决定进入自营详情页或展销详情页，两类页面后续可分别扩展业务逻辑。
			handleGoodsClick(goods) {
				const routeName = goods.goodsType === 2 ? 'MallStoreExhibitionGoodsDetail' : 'MallStoreSelfGoodsDetail'
				this.$router.push({ name: routeName, params: { id: goods.goodsId } })
			},
			handleImageError(event) {
				event.target.src = DEFAULT_GOODS_IMAGE
			}
		}
	}
</script>

<style lang="scss" scoped>
	.mall-store-home {
		position: relative;
		min-height: 700px;
		padding-bottom: 56px;
		overflow: hidden;
		color: #1f2d3d;
		background: radial-gradient(circle at 5% 27%, rgba(60, 148, 225, 0.12), transparent 22%),
			radial-gradient(circle at 96% 72%, rgba(81, 184, 215, 0.1), transparent 24%),
			linear-gradient(180deg, #f5f9fd 0%, #eef5fb 46%, #f8fbfe 100%);

		&::before,
		&::after {
			position: absolute;
			z-index: 0;
			border-radius: 50%;
			content: '';
			pointer-events: none;
		}

		&::before {
			top: 500px;
			left: -190px;
			width: 430px;
			height: 430px;
			border: 72px solid rgba(57, 139, 211, 0.05);
		}

		&::after {
			right: -150px;
			bottom: 70px;
			width: 360px;
			height: 360px;
			background: rgba(64, 157, 211, 0.05);
		}
	}

	.store-hero {
		position: relative;
		width: 100%;
		overflow: hidden;
		color: #ffffff;
		background: linear-gradient(90deg, rgba(5, 67, 145, 0.12) 1px, transparent 1px),
			linear-gradient(rgba(5, 67, 145, 0.1) 1px, transparent 1px), linear-gradient(125deg, #0755ad 0%, #1476d2 52%, #42a8e8 100%);
		background-size: 54px 54px, 54px 54px, 100% 100%;
		box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.18), 0 18px 45px rgba(22, 98, 180, 0.2);

		&::before {
			position: absolute;
			top: -220px;
			left: calc(50% - 850px);
			width: 560px;
			height: 560px;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.07);
			content: '';
		}

		&::after {
			position: absolute;
			right: calc(50% - 830px);
			bottom: -260px;
			width: 520px;
			height: 520px;
			border: 1px solid rgba(255, 255, 255, 0.13);
			border-radius: 50%;
			content: '';
		}
	}

	.hero-inner {
		position: relative;
		z-index: 1;
		display: flex;
		justify-content: space-between;
		box-sizing: border-box;
		width: $main-page-width;
		min-height: 390px;
		margin: 0 auto;
		padding: 58px 38px 58px 52px;
	}

	.hero-content {
		position: relative;
		z-index: 2;
		width: 720px;

		.hero-eyebrow {
			display: block;
			margin-bottom: 12px;
			font-size: 14px;
			letter-spacing: 2px;
			opacity: 0.75;
		}

		h1 {
			margin: 0;
			font-size: 38px;
			line-height: 1.35;
			letter-spacing: 2px;
		}

		> p {
			margin: 14px 0 30px;
			font-size: 17px;
			line-height: 28px;
			opacity: 0.9;
		}
	}

	.hero-search {
		display: flex;
		align-items: center;
		width: 680px;
		height: 58px;
		padding-left: 20px;
		overflow: hidden;
		border: 4px solid rgba(255, 255, 255, 0.22);
		border-radius: 12px;
		background: #ffffff;
		box-shadow: 0 16px 35px rgba(4, 59, 121, 0.2);

		> i {
			color: #8b99aa;
			font-size: 20px;
		}

		input {
			flex: 1;
			height: 100%;
			padding: 0 16px;
			border: 0;
			outline: 0;
			color: #2d3a4b;
			font-size: 16px;

			&::placeholder {
				color: #a0aaba;
			}
		}

		button {
			align-self: stretch;
			width: 112px;
			border: 0;
			color: #ffffff;
			font-size: 16px;
			background: linear-gradient(135deg, #ff9a3d, #f47716);
			cursor: pointer;
			transition: filter 0.2s ease, transform 0.2s ease;

			&:hover:not(:disabled),
			&:focus-visible:not(:disabled) {
				filter: brightness(1.06);
				transform: translateX(-1px);
			}

			&:disabled {
				cursor: wait;
				opacity: 0.72;
			}
		}
	}

	.hot-keywords {
		display: flex;
		align-items: center;
		margin-top: 15px;

		span,
		button {
			font-size: 14px;
		}

		span {
			margin-right: 12px;
			opacity: 0.72;
		}

		button {
			margin-right: 18px;
			padding: 0;
			border: 0;
			color: #ffffff;
			background: transparent;
			cursor: pointer;
			opacity: 0.9;

			&:hover {
				text-decoration: underline;
			}
		}
	}

	.hero-visual {
		position: relative;
		z-index: 1;
		flex: 1;
		min-width: 350px;

		.visual-ring {
			position: absolute;
			border: 1px solid rgba(255, 255, 255, 0.2);
			border-radius: 50%;
		}

		.ring-one {
			top: -8px;
			right: -10px;
			width: 310px;
			height: 310px;
		}

		.ring-two {
			top: 36px;
			right: 34px;
			width: 220px;
			height: 220px;
		}
	}

	.visual-card {
		position: absolute;
		border: 1px solid rgba(255, 255, 255, 0.32);
		background: rgba(255, 255, 255, 0.16);
		box-shadow: 0 16px 35px rgba(1, 60, 127, 0.22);
		backdrop-filter: blur(8px);
	}

	.visual-card-main {
		top: 45px;
		right: 60px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		width: 180px;
		height: 180px;
		border-radius: 26px;

		i {
			margin-bottom: 16px;
			font-size: 50px;
		}

		strong {
			font-size: 20px;
		}

		span {
			margin-top: 7px;
			font-size: 14px;
			opacity: 0.75;
		}
	}

	.visual-card-small {
		display: flex;
		align-items: center;
		padding: 13px 18px;
		border-radius: 12px;
		font-size: 15px;

		i {
			margin-right: 8px;
			font-size: 20px;
		}
	}

	.card-self {
		top: 26px;
		right: 222px;
	}

	.card-show {
		top: 218px;
		right: 10px;
	}

	.store-main {
		position: relative;
		z-index: 1;
		width: $main-page-width;
		margin: 0 auto;
	}

	.service-feature-list {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		margin: 28px 0 42px;
		overflow: hidden;
		border: 1px solid rgba(210, 226, 240, 0.95);
		border-radius: 16px;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(246, 251, 255, 0.98));
		box-shadow: 0 12px 30px rgba(43, 87, 132, 0.08);
	}

	.service-feature {
		position: relative;
		display: flex;
		align-items: center;
		padding: 24px 34px;
		transition: background-color 0.2s ease;

		&:hover {
			background: rgba(235, 246, 255, 0.72);
		}

		&:not(:last-child) {
			border-right: 1px solid #e7edf4;
		}

		.feature-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			flex: 0 0 48px;
			height: 48px;
			margin-right: 15px;
			border-radius: 14px;
			color: #176fc8;
			font-size: 24px;
			background: linear-gradient(145deg, #edf7ff, #dceeff);
			box-shadow: inset 0 0 0 1px rgba(90, 155, 215, 0.08);
		}

		strong {
			font-size: 16px;
		}

		p {
			margin: 6px 0 0;
			color: #7b8796;
			font-size: 14px;
		}
	}

	.section-heading {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		margin-bottom: 22px;

		.section-kicker {
			color: #2878c8;
			font-size: 14px;
			letter-spacing: 1.5px;
		}

		h2 {
			margin: 6px 0;
			font-size: 28px;
		}

		p {
			margin: 0;
			color: #7d8997;
			font-size: 14px;
		}
	}

	.goods-section {
		position: relative;
		padding: 30px;
		overflow: hidden;
		border: 1px solid rgba(210, 226, 240, 0.95);
		border-radius: 18px;
		background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(245, 250, 255, 0.98));
		box-shadow: 0 16px 38px rgba(42, 84, 126, 0.09);

		&::before {
			position: absolute;
			top: -100px;
			right: -90px;
			width: 260px;
			height: 260px;
			border-radius: 50%;
			background: radial-gradient(circle, rgba(84, 169, 229, 0.1), rgba(84, 169, 229, 0));
			content: '';
			pointer-events: none;
		}

		> * {
			position: relative;
			z-index: 1;
		}
	}

	.type-tabs {
		display: flex;
		gap: 8px;
		padding: 5px;
		border: 1px solid #d3e2f0;
		border-radius: 24px;
		background: linear-gradient(180deg, #eef5fb, #e6eef6);
		box-shadow: inset 0 1px 3px rgba(45, 83, 121, 0.06);

		button {
			min-width: 106px;
			height: 38px;
			padding: 0 18px;
			border: 1px solid transparent;
			border-radius: 19px;
			outline: none;
			color: #637083;
			font-family: inherit;
			font-size: 14px;
			background: transparent;
			cursor: pointer;
			transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

			&:hover,
			&:focus-visible {
				border-color: #b8d4ec;
				color: #176fc8;
				background: rgba(255, 255, 255, 0.78);
				transform: translateY(-1px);
			}

			&.active {
				border-color: #2b7ecc;
				color: #ffffff;
				background: linear-gradient(135deg, #4394dd, #176fc8);
				box-shadow: 0 6px 14px rgba(23, 111, 200, 0.24);
				transform: none;
			}
		}
	}

	.goods-list {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 22px;
		min-height: 280px;
	}

	.goods-card {
		overflow: hidden;
		border: 1px solid #e3e9f0;
		border-radius: 12px;
		outline: none;
		background: #ffffff;
		box-shadow: 0 6px 20px rgba(51, 80, 115, 0.05);
		cursor: pointer;
		transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

		&:hover,
		&:focus {
			border-color: #9cc5ed;
			box-shadow: 0 16px 34px rgba(30, 91, 157, 0.14);
			transform: translateY(-6px);

			.goods-image-wrap img {
				transform: scale(1.05);
			}

			.detail-link {
				color: #146fc9;
			}
		}
	}

	.goods-image-wrap {
		position: relative;
		height: 205px;
		overflow: hidden;
		background: #edf2f7;

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
			transition: transform 0.35s ease;
		}
	}

	.goods-type {
		position: absolute;
		top: 14px;
		left: 14px;
		padding: 7px 11px;
		border-radius: 5px;
		color: #ffffff;
		font-size: 14px;
		box-shadow: 0 5px 12px rgba(21, 62, 107, 0.18);

		i {
			margin-right: 4px;
		}

		&.is-self {
			background: #146fc9;
		}

		&.is-exhibition {
			background: #e88625;
		}
	}

	.goods-body {
		padding: 20px;

		.goods-category {
			display: inline-block;
			max-width: 100%;
			padding: 4px 9px;
			overflow: hidden;
			border-radius: 4px;
			color: #54708c;
			font-size: 14px;
			white-space: nowrap;
			text-overflow: ellipsis;
			background: #f1f5f9;
		}

		h3 {
			margin: 12px 0 7px;
			overflow: hidden;
			font-size: 18px;
			line-height: 25px;
		}

		.goods-summary {
			display: -webkit-box;
			margin: 0;
			overflow: hidden;
			color: #7c8794;
			font-size: 14px;
			line-height: 22px;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
		}
	}

	.goods-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 36px;
		margin-top: 18px;
		padding-top: 16px;
		border-top: 1px solid #edf0f4;
	}

	.goods-price {
		color: #ef6c25;

		small {
			font-size: 14px;
		}

		strong {
			font-size: 22px;
		}

		span {
			margin-left: 3px;
			color: #9199a5;
			font-size: 14px;
		}
	}

	.consult-tip {
		color: #d8781e;
		font-size: 14px;

		i {
			margin-right: 5px;
			font-size: 17px;
		}
	}

	.detail-link {
		color: #778493;
		font-size: 14px;
		transition: color 0.2s ease;
	}

	.goods-empty {
		grid-column: 1 / -1;
		padding: 70px 0;
		text-align: center;

		> span {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 66px;
			height: 66px;
			margin: 0 auto 16px;
			border-radius: 50%;
			color: #7fa9d5;
			font-size: 30px;
			background: #e8f1fa;
		}

		strong {
			font-size: 17px;
		}

		p {
			margin: 8px 0 18px;
			color: #8a95a3;
			font-size: 14px;
		}
	}

	@media (max-width: 1400px) {
		.hero-inner,
		.store-main {
			width: calc(100% - 48px);
		}

		.hero-content {
			width: 64%;
		}

		.hero-search {
			width: 100%;
		}
	}

	@media (max-width: 1050px) {
		.hero-visual {
			display: none;
		}

		.hero-content {
			width: 100%;
		}

		.goods-list {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.mall-store-home *,
		.mall-store-home *::before,
		.mall-store-home *::after {
			transition-duration: 0.01ms !important;
			animation-duration: 0.01ms !important;
		}
	}
</style>
